import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../../contexts/AuthContext";
import {truncate} from "../../../TSsupport";
import CFASNavbar from "../../../common/SACC/CareersFairContext/CFASNavbar";
import CFASFooter from "../../../common/SACC/CareersFairContext/CFASFooter";
import {previewEnabled, fetchPublicRoles} from "../../../features/roles/roles";

const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

const reasonMap = {
    "courseArea": "Course area",
    "level": "Education level",
    "gender": "Gender",
    "isOpen": "Applications closed",
};

// Role descriptions can contain a "//" marker; only the part before it is shown, capped at 500 characters.
function shortDescription(text) {
    if (!text) return "";
    const cut = text.includes("//") ? text.substring(0, text.indexOf("//")) : text;
    return truncate(cut, 500);
}

function initials(name) {
    if (!name) return "";
    return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

const ApplyCompany = (props) => {
    const navigate = useNavigate();
    const {checkEligiblePos, companyInfo} = useAuth();

    const [companyData, setCompanyData] = useState({});
    const [rolesData, setRolesData] = useState({});
    const [rolesLoaded, setRolesLoaded] = useState(false);
    const [error, setError] = useState("");
    const [logoFailed, setLogoFailed] = useState(false);

    function handleAuthError(data, status) {
        if ((status === 401 || data?.data?.["reason"] === "Invalid Token") && !isLocalhost) {
            // Not signed in: go to sign-in, then come back to this company (Main keeps the companyID param).
            window.location.href = "/cfas/hub/sign-in?nav=/cfas/apply/company&" + window.location.search.slice(1);
            return true;
        }
        return false;
    }

    useEffect(() => {
        const companyID = new URLSearchParams(window.location.search).get("companyID");
        props.setShowCover(false);
        
        if (previewEnabled) {
            // Frontend preview: build the page from the example roles instead of the server.
            fetchPublicRoles().then((all) => {
                const matches = all.filter((r) => [r.company?.id, r.company?.name].includes(companyID));
                const list = matches.length ? matches : all.filter((r) => r.company?.name === all[0]?.company?.name);
                const company = list[0]?.company || {};
                setCompanyData({
                    "company-name": company.name || "Example Company",
                    "logo": company.logoUrl || "",
                    "company-description": "Example company for frontend preview.",
                });
                setRolesData({
                    eligible: list.map((r) => ({
                        "position-id": r.id,
                        "position-name": r.title,
                        "position-description": r.description || "",
                        "applied": false,
                    })),
                    notEligible: [],
                });
                setRolesLoaded(true);
            }).catch((err) => setError(err.message));
            return;
        }

        companyInfo(companyID).then(async (res) => {
            const data = await res.json();
            if (res.status === 200) {
                setCompanyData(data.data);
            } else if (!handleAuthError(data, res.status)) {
                setError(data?.data?.["reason"] || "We couldn't load this company.");
            }
        }).catch((err) => {
            console.log(err);
            setError("We couldn't reach the server. Check your connection and try again.");
        });

        checkEligiblePos(companyID).then(async (res) => {
            const data = await res.json();
            if (res.status === 200) {
                setRolesData(data.data);
                setRolesLoaded(true);
            } else if (!handleAuthError(data, res.status)) {
                setError(data?.data?.["reason"] || "We couldn't load the roles for this company.");
            }
        }).catch((err) => {
            console.log(err);
            setError("We couldn't reach the server. Check your connection and try again.");
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleClick(roleID) {
        window.location = "/cfas/apply/submit?roleID=" + roleID;
    }

    const companyName = companyData["company-name"] || "";
    const companyDescription = companyData["company-description-long"] || companyData["company-description"] || "";
    const eligible = rolesData["eligible"] || [];
    const notEligible = rolesData["notEligible"] || [];

    const Logo = () => (
        <div className="ms-logo">
            {companyData["logo"] && !logoFailed
                ? <img src={companyData["logo"]} alt={companyName + " logo"} onError={() => setLogoFailed(true)}/>
                : <span>{initials(companyName)}</span>}
        </div>
    );

    const RoleCard = ({role, isEligible}) => (
        <article className="ms-card ms-role">
            <div className="ms-role-head">
                <Logo/>
                <div className="ms-grow">
                    <h2>{role["position-name"]}</h2>
                    <p className="ms-sub">{companyName}</p>
                </div>
            </div>

            <div className="ms-tagline">
                {isEligible && !role["applied"] && <>
                    <span className="ms-tag ok">Eligible</span>
                    <span className="ms-muted ms-small">Your CFAS profile and CV go straight to the recruiter.</span>
                </>}
                {isEligible && role["applied"] && <>
                    <span className="ms-tag info">Already applied</span>
                    <span className="ms-muted ms-small">Track it in My applications.</span>
                </>}
                {!isEligible && <>
                    <span className="ms-tag warn">
                        Not eligible{reasonMap[role["not-eligible-reason"]] ? ": " + reasonMap[role["not-eligible-reason"]] : ""}
                    </span>
                    <span className="ms-muted ms-small">You can still view the details and apply.</span>
                </>}
            </div>

            <div>
                {isEligible && !role["applied"] &&
                    <button className="ms-btn p" onClick={() => handleClick(role["position-id"])}>Apply</button>}
                {isEligible && role["applied"] &&
                    <button className="ms-btn s" onClick={() => navigate("/cfas/track/home")}>My applications</button>}
                {!isEligible &&
                    <button className="ms-btn s" onClick={() => handleClick(role["position-id"])}>View details and apply anyway</button>}
            </div>

            <dl className="ms-dl">
                <dt>About the role</dt>
                <dd dangerouslySetInnerHTML={{__html: shortDescription(role["position-description"])}}/>
            </dl>
        </article>
    );

    return (
        <div className="ms">
            <style>{CSS}</style>

            <CFASNavbar/>

            <main className="ms-main">
                <div className="ms-in">
                    <div className="ms-narrow ms-stack">

                        {/* Breadcrumb */}
                        <nav className="ms-crumbs" aria-label="Breadcrumb">
                            <button className="ms-link" onClick={() => navigate("/cfas/apply/home")}>Find roles</button>
                            <span aria-hidden="true">/</span>
                            <span>{companyName || "Company"}</span>
                        </nav>

                        {error && <div className="ms-banner err" role="alert">{error}</div>}

                        {/* Company */}
                        {(companyName || companyDescription) && (
                            <section className="ms-card ms-company">
                                <div className="ms-role-head">
                                    <Logo/>
                                    <h1>{companyName}</h1>
                                </div>
                                {companyDescription &&
                                    <div className="ms-desc ms-muted" dangerouslySetInnerHTML={{__html: companyDescription}}/>}
                            </section>
                        )}

                        {/* Eligible roles */}
                        <h2 className="ms-sec-title">Open roles</h2>
                        {!rolesLoaded && !error && (
                            <div className="ms-card ms-role">
                                <div className="ms-skel-line w60 tall"/>
                                <div className="ms-skel-line w40"/>
                                <p className="ms-muted ms-small">Loading roles…</p>
                            </div>
                        )}
                        {rolesLoaded && eligible.length === 0 && (
                            <div className="ms-card ms-empty">
                                <h3>No roles you're eligible for yet</h3>
                                <p className="ms-muted">Check the other roles below, or browse other employers.</p>
                                <button className="ms-btn s" onClick={() => navigate("/cfas/apply/home")}>Find roles</button>
                            </div>
                        )}
                        {rolesLoaded && eligible.map((role) =>
                            <RoleCard key={role["position-id"]} role={role} isEligible={true}/>)}

                        {/* Other roles */}
                        {rolesLoaded && notEligible.length > 0 && <>
                            <h2 className="ms-sec-title">Other roles</h2>
                            {notEligible.map((role) =>
                                <RoleCard key={role["position-id"]} role={role} isEligible={false}/>)}
                        </>}
                    </div>
                </div>
            </main>

            <CFASFooter/>
        </div>
    );
};

const CSS = `
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Figtree:wght@400;500;600;700&display=swap");
.ms{
  --green:#004225;--deep:#002B1B;--sage:#A7B8A5;--gold:#C3A56A;--gold-deep:#8E7340;--gold-soft:#F3EAD6;
  --cream:#F4EFE5;--ink:#10251C;--muted:#5B6B62;--line:#E1E6DD;--soft:#E6EEE6;
  --ok:#2E7D4F;--ok-soft:#DDF0E3;--warn:#A8651A;--warn-soft:#F8EAD6;--info:#2B4C7E;--info-soft:#E3EAF5;
  --err:#B3261E;--err-soft:#FBE9E7;
  --sh:0 1px 2px rgba(0,43,27,.05),0 10px 28px rgba(0,43,27,.08);
  --f-display:"soleil","Soleil","Outfit",system-ui,sans-serif;
  --f-body:"proxima-nova","Proxima Nova","Figtree",system-ui,sans-serif;--r:14px;
  background:var(--cream);color:var(--ink);font-family:var(--f-body);font-size:16px;line-height:1.55;min-height:100vh;
  /* Full-bleed: break out of CFASWrapper's boxed width so the page spans the whole viewport */
  width:100vw;max-width:100vw;margin-left:calc(50% - 50vw);margin-right:calc(50% - 50vw);overflow-x:clip;
  /* Room for the fixed nav; column layout keeps the footer at the bottom */
  padding-top:66px;display:flex;flex-direction:column;
}
html:has(.ms){overflow-x:hidden}
.ms .cfn{position:fixed;top:0;left:0;right:0;z-index:1000}

.ms *{box-sizing:border-box}
.ms h1,.ms h2,.ms h3{font-family:var(--f-display);color:var(--deep);margin:0;line-height:1.15}
.ms h1{font-size:clamp(1.5rem,3vw,1.9rem);font-weight:700;letter-spacing:-.01em}
.ms h2{font-size:clamp(1.25rem,2.2vw,1.45rem);font-weight:700;letter-spacing:-.005em}
.ms h3{font-size:1.1rem;font-weight:700}
.ms p{margin:0}
.ms-muted{color:var(--muted)}.ms-small{font-size:.88rem}
.ms-in{max-width:1120px;margin:0 auto;padding-inline:20px}
.ms-grow{flex:1;min-width:0}

/* layout */
.ms-main{padding-block:36px 80px;flex:1}
.ms-narrow{max-width:720px}
.ms-stack{display:grid;gap:18px;min-width:0;align-content:start}
.ms-card{background:#fff;border-radius:var(--r);padding:22px 24px;display:grid;gap:14px;min-width:0;box-shadow:var(--sh)}
.ms-sec-title{margin-top:14px}

/* breadcrumb */
.ms-crumbs{display:flex;align-items:center;gap:6px;font-size:.92rem;color:var(--muted)}
.ms-link{all:unset;cursor:pointer;color:var(--deep);font-weight:600;text-decoration:underline;text-underline-offset:3px}
.ms-link:focus-visible{outline:2px solid var(--gold);outline-offset:2px}

/* company + role cards */
.ms-role-head{display:flex;align-items:center;gap:16px}
.ms-logo{width:56px;height:56px;flex:none;border-radius:12px;border:1px solid var(--line);background:#fff;display:grid;place-items:center;overflow:hidden}
.ms-logo img{width:88%;height:88%;object-fit:contain;display:block}
.ms-logo span{font-family:var(--f-display);font-weight:800;font-size:.85rem;color:var(--green)}
.ms-sub{color:var(--muted);margin-top:2px}
.ms-desc{white-space:pre-wrap;font-size:.98rem}
.ms-tagline{display:flex;align-items:center;flex-wrap:wrap;gap:8px 12px}

/* tags */
.ms-tag{display:inline-flex;align-items:center;gap:6px;font-size:.78rem;font-weight:700;padding:5px 11px;border-radius:999px;white-space:nowrap}
.ms-tag::before{content:"";width:6px;height:6px;border-radius:50%;background:currentColor}
.ms-tag.ok{background:var(--ok-soft);color:var(--ok)}
.ms-tag.warn{background:var(--warn-soft);color:var(--warn)}
.ms-tag.info{background:var(--info-soft);color:var(--info)}

/* definition list */
.ms-dl{display:grid;grid-template-columns:140px 1fr;gap:10px 20px;margin:0;font-size:.98rem}
.ms-dl dt{font-weight:700;color:var(--deep)}
.ms-dl dd{margin:0;color:var(--ink);white-space:pre-wrap}
@media (max-width:560px){.ms-dl{grid-template-columns:1fr;gap:4px}}

/* buttons */
.ms-btn{all:unset;cursor:pointer;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-family:var(--f-display);font-weight:700;font-size:.98rem;min-height:46px;padding:10px 22px;border-radius:12px;border:1.5px solid transparent;white-space:nowrap;transition:transform .12s}
.ms-btn:hover{transform:translateY(-1px)}
.ms-btn:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
.ms-btn.p{background:var(--deep);color:#fff;box-shadow:0 6px 18px rgba(0,43,27,.28)}
.ms-btn.s{background:#fff;color:var(--deep);border-color:var(--deep)}

/* states */
.ms-banner{padding:12px 14px;border-radius:12px;font-size:.92rem}
.ms-banner.err{background:var(--err-soft);color:var(--err)}
.ms-empty{text-align:center;justify-items:center;padding:32px 24px}
.ms-skel-line{height:14px;border-radius:7px;background:var(--soft);animation:ms-pulse 1.2s ease-in-out infinite}
.ms-skel-line.tall{height:26px}
.ms-skel-line.w60{width:60%}.ms-skel-line.w40{width:40%}
@keyframes ms-pulse{50%{opacity:.5}}
@media (prefers-reduced-motion:reduce){.ms-skel-line{animation:none}.ms-btn{transition:none}}
`;

export default ApplyCompany;